import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import {
  MdOutlineInventory2,
  MdOutlineStorefront,
  MdCheck,
  MdOutlineWarningAmber,
} from "react-icons/md";
import noImage from "../../../../assets/images/noImage.png";
import { fetchOrderItems } from "../../../../services/orders.services";

const OrderItemsSkeleton = () => (
  <div className="space-y-2 py-1">
    {[1, 2, 3].map((item) => (
      <div key={item} className="flex items-center gap-2.5">
        <div className="h-9 w-9 shrink-0 animate-pulse rounded-md bg-ui-border" />

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="h-2.5 w-2/3 animate-pulse rounded bg-ui-border" />
          <div className="h-2 w-1/3 animate-pulse rounded bg-ui-border-subtle" />
        </div>

        <div className="h-4 w-8 shrink-0 animate-pulse rounded bg-ui-border" />
      </div>
    ))}
  </div>
);

const FulfillmentStatus = ({ product, quantity, orderStatusId }) => {
  const isBoltProduct = !product?.plugin_item_id;
  // Fulfillment status only applies to Bolt products.
  if (!isBoltProduct) return null;

  // Status 4: all Bolt products are fulfilled.
  if (orderStatusId === 4) {
    return (
      <div
        className="flex shrink-0 items-center gap-0.5 rounded border border-state-success-border bg-state-success-bg px-1.5 py-0.5 text-[9px] font-medium leading-none text-state-success-text"
        title={`${quantity} of ${quantity} item(s) fulfilled`}
      >
        <MdCheck className="h-2.5 w-2.5" />
        <span>
          {quantity}/{quantity}
        </span>
      </div>
    );
  }

  if (orderStatusId === 5) {
    const fulfilledQuantity = Number(product?.saleable_item_quantity ?? 0);

    const totalQuantity = Number(quantity ?? 0);

    // Fully fulfilled
    if (String(product?.is_saleable) === "1") {
      return (
        <div
          className="flex shrink-0 items-center gap-0.5 rounded border border-state-success-border bg-state-success-bg px-1.5 py-0.5 text-[9px] font-medium leading-none text-state-success-text"
          title={`${fulfilledQuantity} of ${quantity} item(s) fulfilled`}
        >
          <MdCheck className="h-2.5 w-2.5" />
          <span>
            {fulfilledQuantity}/{quantity}
          </span>
        </div>
      );
    }

    // Partial fulfillment
    if (fulfilledQuantity > 0 && fulfilledQuantity < totalQuantity) {
      return (
        <div
          className="flex shrink-0 items-center gap-0.5 rounded border border-state-warning-border bg-state-warning-bg px-1.5 py-0.5 text-[9px] font-medium leading-none text-state-warning-text"
          title={`${fulfilledQuantity} of ${quantity} item(s) fulfilled`}
        >
          <MdOutlineWarningAmber className="h-2.5 w-2.5" />
          <span>
            Partially Fulfilled {fulfilledQuantity}/{quantity}
          </span>
        </div>
      );
    }

    // No fulfillment / requires attention
    return (
      <div
        className="flex shrink-0 items-center gap-0.5 rounded border border-state-warning-border bg-state-warning-bg px-1.5 py-0.5 text-[9px] font-medium leading-none text-state-warning-text"
        title="Fulfillment requires attention"
      >
        <MdOutlineWarningAmber className="h-2.5 w-2.5" />
        <span>Attention</span>
      </div>
    );
  }

  return null;
};

const ProductRow = ({ product, orderStatusId }) => {
  const image =
    product?.image ||
    product?.shopify_image ||
    product?.products?.media?.[0]?.file ||
    noImage;

  const name =
    product?.name || product?.product_name || product?.products?.name || "-";

  const sku = product?.sku || product?.product_sku || product?.products?.sku;

  const quantity = product?.quantity ?? product?.item_quantity ?? 0;

  const weight = product?.weight;

  const weightUnit =
    product?.products?.weight_unit || product?.weightUnit || "";

  const length = product?.length;
  const width = product?.width;
  const height = product?.height;

  const dimensionUnit =
    product?.products?.dimension_unit || product?.dimensionUnit || "";

  const hasDimensions = length != null && width != null && height != null;

  return (
    <div className="flex items-start gap-2.5 rounded-lg bg-ui-page px-2 py-1.5 shadow-ui-sm transition-colors duration-150 hover:bg-ui-row-hover">
      {/* Product Image */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border bg-ui-surface">
        <img
          src={image}
          alt=""
          loading="lazy"
          className="h-full w-full object-contain"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = noImage;
          }}
        />
      </div>

      {/* Product Information */}
      <div className="min-w-0 flex-1 overflow-hidden">
        <p className="mb-0 break-words text-[11px] font-medium leading-[1.25] text-ui-text">
          {name}
        </p>

        {(sku || weight != null || hasDimensions) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[9px] leading-none text-ui-text-muted">
            {sku && <span>SKU: {sku}</span>}

            {weight != null && (
              <span>
                {weight} {weightUnit}
              </span>
            )}

            {hasDimensions && (
              <span>
                {length}×{width}×{height} {dimensionUnit}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Fulfillment Status + Quantity */}
      <div className="flex shrink-0 items-center gap-1">
        <FulfillmentStatus
          product={product}
          quantity={quantity}
          orderStatusId={orderStatusId}
        />

        {/* Ordered Quantity */}
        <div className="rounded border border-ui-border px-1.5 py-0.5 text-[10px] font-medium leading-none text-ui-text-muted">
          ×{quantity}
        </div>
      </div>
    </div>
  );
};

const ProductSection = ({ icon: Icon, title, products, orderStatusId }) => {
  if (!products?.length) return null;

  return (
    <div className="mb-1 last:mb-0">
      {/* Section Header */}
      <div className="mb-1.5 flex items-center gap-1.5 px-1.5 py-1.5">
        <Icon className="h-3 w-3 text-ui-text-subtle" />

        <span className="text-[10px] font-semibold uppercase leading-none tracking-wide text-ui-text-muted">
          {title}
        </span>

        <span className="text-[10px] leading-none text-ui-text-subtle">
          ({products.length})
        </span>
      </div>

      {/* Product List */}
      <div className="space-y-1.5">
        {products.map((product, index) => (
          <ProductRow
            key={product?.id ?? index}
            product={product}
            orderStatusId={orderStatusId}
          />
        ))}
      </div>
    </div>
  );
};

const OrderItemsModal = ({
  open,
  onClose,
  orderId,
  orderCode,
  orderStatusId,
}) => {
  const [boltProducts, setBoltProducts] = useState([]);
  const [marketplaceProducts, setMarketplaceProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalizedOrderStatusId = Number(orderStatusId);

  useEffect(() => {
    if (!open || !orderId) return;

    let active = true;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setBoltProducts([]);
        setMarketplaceProducts([]);

        const response = await fetchOrderItems(orderId);

        if (!active) return;

        const items = response?.data?.result ?? [];

        /*
         * Marketplace products have plugin_item_id.
         * Everything else is considered a Bolt product.
         */
        const marketplaceItems = items.filter((item) => item?.plugin_item_id);

        const boltItems = items.filter((item) => !item?.plugin_item_id);

        setMarketplaceProducts(marketplaceItems);

        setBoltProducts(boltItems);
      } catch (error) {
        if (!active) return;

        console.error("Failed to fetch order products:", error);

        setBoltProducts([]);
        setMarketplaceProducts([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      active = false;
    };
  }, [open, orderId]);

  const totalItems = boltProducts.length + marketplaceProducts.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "rounded-md bg-ui-surface",
        sx: {
          backgroundColor: "var(--color-ui-surface)",
        },
      }}
    >
      <div className="flex max-h-[70vh] flex-col bg-ui-surface">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-ui-border bg-ui-surface px-3.5 py-2.5">
          <div className="min-w-0">
            <h2 className="mb-0 truncate pt-2 text-[13px] font-semibold leading-tight text-ui-text">
              Order Items{" "}
              <span className="font-normal">({orderCode || "-"})</span>
            </h2>

            <p className="mb-0 mt-0.5 text-[10px] leading-tight text-ui-text-muted">
              {loading
                ? "Loading products..."
                : `${totalItems} ${totalItems === 1 ? "item" : "items"}`}
            </p>
          </div>

          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            className="group flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-brand-light bg-ui-surface transition-all duration-150 hover:!bg-brand"
          >
            <RxCross2 className="h-3 w-3 text-ui-text-muted transition-colors duration-150 group-hover:text-white" />
          </button>
        </div>

        {/* Products */}
        <div className="min-h-[180px] overflow-y-auto bg-ui-surface px-2.5 py-1.5">
          {loading ? (
            <div className="px-1.5">
              <OrderItemsSkeleton />
            </div>
          ) : totalItems > 0 ? (
            <div>
              {/* Marketplace Products */}
              <ProductSection
                icon={MdOutlineStorefront}
                title="Marketplace Products"
                products={marketplaceProducts}
                orderStatusId={normalizedOrderStatusId}
              />

              {/* Bolt Products */}
              <ProductSection
                icon={MdOutlineInventory2}
                title="Bolt Products"
                products={boltProducts}
                orderStatusId={normalizedOrderStatusId}
              />
            </div>
          ) : (
            <div className="flex h-full min-h-[160px] items-center justify-center">
              <p className="mb-0 text-xs text-ui-text-muted">
                No products found.
              </p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default OrderItemsModal;
